/**
 * Season Regulations Data
 *
 * Local data file for state deer season opener/closer dates by weapon type and year.
 * Keyed by state abbreviation → year → SeasonRegulation[].
 *
 * Update policy: refresh annually in spring when states publish regulations (typically March–July).
 * When new data is available, update `lastUpdated` and add the new year's array.
 *
 * Sources:
 *   WI — https://dnr.wisconsin.gov/topic/hunt/dates
 *   MN — https://www.dnr.state.mn.us/hunting/seasons.html
 *   MI — https://www.michigan.gov/dnr/things-to-do/hunting/deer
 *   IA — https://www.iowadnr.gov/things-do/hunting-trapping/types-hunting-trapping/deer-hunting
 *   IL — https://dnr.illinois.gov/hunting/deerhunting.html
 *   OH — https://ohiodnr.gov/discover-and-learn/safety-conservation/about-ODNR/news
 *   PA — https://www.pa.gov/agencies/pgc/newsroom/preliminary-2026-27-hunting-seasons-and-bag-limits-approved
 */

import { WeaponType } from '@/types/season';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SeasonRegulation {
  weaponType: WeaponType;
  openerDate: string; // YYYY-MM-DD
  closerDate: string; // YYYY-MM-DD
  name: string; // e.g., "9-Day Gun Deer"
  notes?: string; // zone restrictions, special conditions, etc.
}

export interface StateRegulationMeta {
  dnrUrl: string; // Link shown in stale-data warning
  lastUpdated: string; // YYYY-MM-DD — date this state's data was last verified against DNR source
}

interface StateRegulationData extends StateRegulationMeta {
  seasons: Record<number, SeasonRegulation[]>; // fall-year → regulations
}

// ─── Wisconsin ───────────────────────────────────────────────────────────────
// SOURCE: https://dnr.wisconsin.gov/topic/hunt/dates  (verified 2026-04-03)

const WI: StateRegulationData = {
  dnrUrl: 'https://dnr.wisconsin.gov/topic/hunt/deer',
  lastUpdated: '2026-04-03',
  seasons: {
    2026: [
      {
        weaponType: WeaponType.Bow,
        openerDate: '2026-09-12',
        closerDate: '2027-01-03',
        name: 'Archery Deer',
        notes: 'Extended to Jan 31 in select metro subunits and counties.',
      },
      {
        weaponType: WeaponType.Crossbow,
        openerDate: '2026-09-12',
        closerDate: '2027-01-03',
        name: 'Crossbow Deer',
        notes: 'Runs concurrently with archery. Extended to Jan 31 in select areas.',
      },
      {
        weaponType: WeaponType.Gun,
        openerDate: '2026-11-21',
        closerDate: '2026-11-29',
        name: '9-Day Gun Deer',
      },
      {
        weaponType: WeaponType.Muzzleloader,
        openerDate: '2026-11-30',
        closerDate: '2026-12-09',
        name: 'Muzzleloader Deer',
      },
      {
        weaponType: WeaponType.Youth,
        openerDate: '2026-10-10',
        closerDate: '2026-10-11',
        name: 'Youth/Disabled Gun Deer Hunt',
      },
    ],
  },
};

// ─── Minnesota ───────────────────────────────────────────────────────────────
// SOURCE: https://www.dnr.state.mn.us/hunting/seasons.html  (verified 2026-04-03)

const MN: StateRegulationData = {
  dnrUrl: 'https://www.dnr.state.mn.us/hunting/deer/index.html',
  lastUpdated: '2026-04-03',
  seasons: {
    2026: [
      {
        weaponType: WeaponType.Bow,
        openerDate: '2026-09-19',
        closerDate: '2026-12-31',
        name: 'Archery Deer',
        notes: 'Statewide.',
      },
      {
        weaponType: WeaponType.Gun,
        openerDate: '2026-11-07',
        closerDate: '2026-11-22',
        name: 'Firearms Season A',
        notes: '100-series permit areas statewide; 200 & 300 series close Nov 15.',
      },
      {
        weaponType: WeaponType.Muzzleloader,
        openerDate: '2026-11-28',
        closerDate: '2026-12-13',
        name: 'Muzzleloader Deer',
        notes: 'Statewide.',
      },
      {
        weaponType: WeaponType.Youth,
        openerDate: '2026-10-15',
        closerDate: '2026-10-18',
        name: 'Youth Deer Hunt',
      },
    ],
  },
};

// ─── Michigan ────────────────────────────────────────────────────────────────
// SOURCE: https://www.michigan.gov/dnr/things-to-do/hunting/deer  (verified 2026-04-03)
// NOTE: Michigan's regular firearm season (Nov 15-30) and archery dates are established
// by statute and are fixed year-over-year. Muzzleloader opens the first Friday in December
// (Dec 4 in 2026). Official 2026 season booklet has not yet been published; verify dates
// at michigan.gov/deer before use.

const MI: StateRegulationData = {
  dnrUrl: 'https://www.michigan.gov/dnr/things-to-do/hunting/deer',
  lastUpdated: '2026-04-03',
  seasons: {
    2026: [
      {
        weaponType: WeaponType.Bow,
        openerDate: '2026-10-01',
        closerDate: '2026-11-14',
        name: 'Early Archery Deer',
        notes: 'Statewide. Season resumes Dec 1 – Jan 1 after firearm season.',
      },
      {
        weaponType: WeaponType.Bow,
        openerDate: '2026-12-01',
        closerDate: '2027-01-01',
        name: 'Late Archery Deer',
        notes: 'Resumes after firearm and muzzleloader seasons.',
      },
      {
        weaponType: WeaponType.Gun,
        openerDate: '2026-11-15',
        closerDate: '2026-11-30',
        name: 'Regular Firearm Deer',
        notes: 'Statewide. Dates set by Michigan statute (MCL 324.43514).',
      },
      {
        weaponType: WeaponType.Muzzleloader,
        openerDate: '2026-12-04',
        closerDate: '2026-12-13',
        name: 'Muzzleloader Deer',
        notes: 'Zones 1, 2, and 3 only. Opens first Friday of December.',
      },
      {
        weaponType: WeaponType.Youth,
        openerDate: '2026-09-12',
        closerDate: '2026-09-13',
        name: 'Youth Liberty Hunt',
        notes: 'Open to youth hunters and hunters with qualifying disabilities.',
      },
    ],
  },
};

// ─── Iowa ────────────────────────────────────────────────────────────────────
// SOURCE: https://www.iowadnr.gov/things-do/hunting-trapping/types-hunting-trapping/deer-hunting
// NOTE: 2026-2027 Iowa deer season dates not yet published as of April 2026.
// Dates below are for the 2025-2026 season only. Update when IDNR publishes fall 2026 dates
// (typically released mid-summer). Add a 2026 key to this object at that time.
// TODO: Add Iowa 2026 season dates (https://www.iowadnr.gov/things-do/hunting-trapping/iowa-hunting-seasons)

const IA: StateRegulationData = {
  dnrUrl: 'https://www.iowadnr.gov/things-do/hunting-trapping/types-hunting-trapping/deer-hunting',
  lastUpdated: '2026-04-03',
  seasons: {
    2025: [
      {
        weaponType: WeaponType.Bow,
        openerDate: '2025-10-01',
        closerDate: '2025-12-05',
        name: 'Archery Deer (Early)',
        notes: 'Archery pauses for gun seasons. Season resumes Dec 22 – Jan 10.',
      },
      {
        weaponType: WeaponType.Bow,
        openerDate: '2025-12-22',
        closerDate: '2026-01-10',
        name: 'Archery Deer (Late)',
      },
      {
        weaponType: WeaponType.Muzzleloader,
        openerDate: '2025-10-11',
        closerDate: '2025-10-19',
        name: 'Early Muzzleloader Deer',
      },
      {
        weaponType: WeaponType.Muzzleloader,
        openerDate: '2025-12-22',
        closerDate: '2026-01-10',
        name: 'Late Muzzleloader Deer',
      },
      {
        weaponType: WeaponType.Gun,
        openerDate: '2025-12-06',
        closerDate: '2025-12-10',
        name: 'First Shotgun Season',
      },
      {
        weaponType: WeaponType.Gun,
        openerDate: '2025-12-13',
        closerDate: '2025-12-21',
        name: 'Second Shotgun Season',
      },
      {
        weaponType: WeaponType.Youth,
        openerDate: '2025-09-20',
        closerDate: '2025-10-05',
        name: 'Youth Deer Season',
      },
    ],
  },
};

// ─── Illinois ────────────────────────────────────────────────────────────────
// SOURCE: https://dnr.illinois.gov/hunting/deerhunting.html  (verified 2026-04-03)

const IL: StateRegulationData = {
  dnrUrl: 'https://dnr.illinois.gov/hunting/deerhunting.html',
  lastUpdated: '2026-04-03',
  seasons: {
    2026: [
      {
        weaponType: WeaponType.Bow,
        openerDate: '2026-10-01',
        closerDate: '2026-11-19',
        name: 'Archery Deer (Early)',
        notes: 'First archery window. Season resumes Nov 23 between firearm seasons.',
      },
      {
        weaponType: WeaponType.Bow,
        openerDate: '2026-11-23',
        closerDate: '2026-12-02',
        name: 'Archery Deer (Mid)',
        notes: 'Between first and second firearm seasons.',
      },
      {
        weaponType: WeaponType.Bow,
        openerDate: '2026-12-07',
        closerDate: '2027-01-17',
        name: 'Archery Deer (Late)',
        notes: 'In Cook, DuPage, Lake and Kane County east of Route 47, archery is open Oct 1 – Jan 17 continuous.',
      },
      {
        weaponType: WeaponType.Gun,
        openerDate: '2026-11-20',
        closerDate: '2026-11-22',
        name: 'First Firearm Season',
      },
      {
        weaponType: WeaponType.Gun,
        openerDate: '2026-12-03',
        closerDate: '2026-12-06',
        name: 'Second Firearm Season',
      },
      {
        weaponType: WeaponType.Muzzleloader,
        openerDate: '2026-12-11',
        closerDate: '2026-12-13',
        name: 'Muzzleloader Deer',
      },
    ],
  },
};

// ─── Ohio ─────────────────────────────────────────────────────────────────────
// SOURCE: https://ohiodnr.gov/discover-and-learn/safety-conservation/about-ODNR/news
// NOTE: Ohio 2026-2027 deer season dates not yet published as of April 2026.
// Dates below are for the 2025-2026 season only. Update when ODNR publishes fall 2026 dates
// (typically released spring/summer). Add a 2026 key to this object at that time.
// TODO: Add Ohio 2026 season dates (https://ohiodnr.gov/discover-and-learn/safety-conservation/about-ODNR/news)

const OH: StateRegulationData = {
  dnrUrl: 'https://ohiodnr.gov/discover-and-learn/safety-conservation/about-ODNR/news',
  lastUpdated: '2026-04-03',
  seasons: {
    2025: [
      {
        weaponType: WeaponType.Bow,
        openerDate: '2025-09-27',
        closerDate: '2026-02-01',
        name: 'Archery Deer',
        notes: 'Statewide archery season.',
      },
      {
        weaponType: WeaponType.Gun,
        openerDate: '2025-12-01',
        closerDate: '2025-12-07',
        name: 'Firearms Deer (First)',
      },
      {
        weaponType: WeaponType.Gun,
        openerDate: '2025-12-20',
        closerDate: '2025-12-21',
        name: 'Firearms Deer (Second)',
      },
      {
        weaponType: WeaponType.Muzzleloader,
        openerDate: '2026-01-03',
        closerDate: '2026-01-06',
        name: 'Muzzleloader Deer',
      },
      {
        weaponType: WeaponType.Youth,
        openerDate: '2025-11-22',
        closerDate: '2025-11-23',
        name: 'Youth Deer Gun Season',
      },
    ],
  },
};

// ─── Pennsylvania ────────────────────────────────────────────────────────────
// SOURCE: https://www.pa.gov/agencies/pgc/newsroom/preliminary-2026-27-hunting-seasons-and-bag-limits-approved
// (verified 2026-04-03) — preliminary 2026-27 seasons approved by PA Game Commission

const PA: StateRegulationData = {
  dnrUrl: 'https://www.pgc.pa.gov/HuntTrap/Pages/DeerHunting.aspx',
  lastUpdated: '2026-04-03',
  seasons: {
    2026: [
      {
        weaponType: WeaponType.Bow,
        openerDate: '2026-10-03',
        closerDate: '2026-11-20',
        name: 'Archery Deer',
        notes: 'Statewide. WMUs 2B, 5C, 5D open Sep 19. Archery resumes Dec 26 – Jan 24, 2027.',
      },
      {
        weaponType: WeaponType.Gun,
        openerDate: '2026-11-28',
        closerDate: '2026-12-13',
        name: 'Regular Firearms Deer',
        notes: 'Statewide antlered and antlerless. Opens Saturday after Thanksgiving.',
      },
      {
        weaponType: WeaponType.Muzzleloader,
        openerDate: '2026-10-17',
        closerDate: '2026-10-25',
        name: 'Muzzleloader Deer (Antlerless)',
        notes: 'Antlerless deer only.',
      },
    ],
  },
};

// ─── Registry ─────────────────────────────────────────────────────────────────

const SEASON_REGULATIONS: Record<string, StateRegulationData> = {
  WI,
  MN,
  MI,
  IA,
  IL,
  OH,
  PA,
};

// ─── Exports ──────────────────────────────────────────────────────────────────

/**
 * Returns season regulations for the given state, weapon type, and year.
 *
 * - Returns `null` if the state is not in the database.
 * - Returns `null` if the year has no data for that state (e.g., not yet published).
 * - Returns `null` if no seasons match the given weapon type.
 *
 * Null return signals the caller to fall back to manual date entry.
 */
export function getSeasonDates(
  state: string,
  weaponType: WeaponType,
  year: number,
): SeasonRegulation[] | null {
  const stateData = SEASON_REGULATIONS[state.toUpperCase()];
  if (!stateData) return null;

  const yearSeasons = stateData.seasons[year];
  if (!yearSeasons) return null;

  const matching = yearSeasons.filter((s) => s.weaponType === weaponType);
  return matching.length > 0 ? matching : null;
}

/**
 * Returns regulation meta (DNR URL, last updated) for the given state.
 * Returns `null` if the state is not in the database.
 * Used to render stale-data warnings and DNR verification links.
 */
export function getStateRegulationMeta(state: string): StateRegulationMeta | null {
  const stateData = SEASON_REGULATIONS[state.toUpperCase()];
  if (!stateData) return null;
  return { dnrUrl: stateData.dnrUrl, lastUpdated: stateData.lastUpdated };
}
